import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_MARK_URL } from "@/data/content";

const LINKS = [
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Our Work" },
    { to: "/process", label: "Process" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${scrolled || open ? "bg-[#0A0D14]/90 backdrop-blur-xl border-white/10" : "bg-[#0A0D14]/60 backdrop-blur-md border-white/5"}`} data-testid="site-header">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 h-20 sm:h-24 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3.5 group" data-testid="nav-logo-link">
                    <img src={LOGO_MARK_URL} alt="Innovative Fusion Hives logo" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl object-cover ring-1 ring-white/20 shadow-[0_4px_20px_rgba(245,158,11,0.15)] group-hover:ring-amber-400/60 transition-shadow" />
                    <span className="hidden sm:block leading-tight">
                        <span className="block font-display font-extrabold text-base sm:text-lg tracking-tight text-white">Innovative Fusion Hives</span>
                        <span className="block font-mono2 text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-amber-400">Where Innovation Thrives</span>
                    </span>
                </Link>
                <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                    {LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) => `text-sm font-medium transition-colors hover:text-white ${isActive ? "text-white" : "text-slate-400"}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <Link to="/contact" data-testid="nav-cta-start-project"
                        className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-500 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-[0_0_28px_rgba(124,58,237,0.5)]">
                        Start Your Project <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <button className="lg:hidden p-2 text-slate-300 hover:text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} data-testid="nav-mobile-menu-button">
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-[#0A0D14]/95 backdrop-blur-xl border-t border-white/5"
                        aria-label="Mobile"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) => `font-display text-2xl font-bold ${isActive ? "text-violet-400" : "text-slate-200"}`}>
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link to="/contact" data-testid="mobile-nav-cta-start-project"
                                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-violet-600 px-6 py-3 font-semibold text-white">
                                Start Your Project <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
